'use client';

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type ToastType = 'success' | 'error' | 'info';

interface ToastModalState {
  isVisible: boolean;
  type: ToastType;
  message: string;
}

interface ToastModalConfig {
  autoClose: boolean;
  autoCloseDelay: number;
}

interface ToastModalContextType {
  showToast: (
    message: string,
    type?: ToastType,
    autoClose?: boolean,
    autoCloseDelay?: number
  ) => void;
  hideToast: () => void;
  isVisible: boolean;
  state: ToastModalState;
  config: ToastModalConfig;
}

const ToastModalContext = createContext<ToastModalContextType | undefined>(undefined);

interface ToastModalProviderProps {
  children: ReactNode;
}

export const ToastModalProvider: React.FC<ToastModalProviderProps> = ({ children }) => {
  const [toastState, setToastState] = useState<ToastModalState>({
    isVisible: false,
    type: 'info',
    message: ''
  });

  const [toastConfig, setToastConfig] = useState({
    autoClose: true,
    autoCloseDelay: 4000
  });

  const showToast = useCallback(
    (
      message: string,
      type: ToastType = 'info',
      autoClose: boolean = true,
      autoCloseDelay: number = 4000
    ) => {
      try {
        // Validar que el mensaje no esté vacío
        if (!message || typeof message !== 'string' || message.trim().length === 0) {
          console.warn('ToastModal: El mensaje no puede estar vacío');
          return;
        }

        // Validar tipo
        const validTypes: ToastType[] = ['success', 'error', 'info'];
        if (!validTypes.includes(type)) {
          console.warn(`ToastModal: Tipo inválido "${type}". Usando "info" por defecto.`);
          type = 'info';
        }

        // Validar autoCloseDelay
        if (autoCloseDelay < 1000 || autoCloseDelay > 30000) {
          console.warn(
            'ToastModal: autoCloseDelay debe estar entre 1000ms y 30000ms. Usando 4000ms por defecto.'
          );
          autoCloseDelay = 4000;
        }

        setToastState({
          isVisible: true,
          type,
          message: message.trim()
        });

        setToastConfig({
          autoClose,
          autoCloseDelay
        });
      } catch (error) {
        console.error('ToastModal: Error al mostrar el toast:', error);
        // Fallback: mostrar un toast de error básico
        setToastState({
          isVisible: true,
          type: 'error',
          message: 'Error al mostrar la notificación'
        });
        setToastConfig({
          autoClose: true,
          autoCloseDelay: 4000
        });
      }
    },
    []
  );

  const hideToast = useCallback(() => {
    try {
      setToastState((prev) => ({
        ...prev,
        isVisible: false
      }));
    } catch (error) {
      console.error('ToastModal: Error al ocultar el toast:', error);
    }
  }, []);

  const contextValue: ToastModalContextType = {
    showToast,
    hideToast,
    isVisible: toastState.isVisible,
    state: toastState,
    config: toastConfig
  };

  return <ToastModalContext.Provider value={contextValue}>{children}</ToastModalContext.Provider>;
};

export const useToastModal = (): ToastModalContextType => {
  const context = useContext(ToastModalContext);

  if (context === undefined) {
    throw new Error('useToastModal debe ser usado dentro de un ToastModalProvider');
  }

  return context;
};

export default ToastModalContext;
